/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/**
 * One page of a grid that pages on the server: which rows to return, in what order, matching which filters.
 * The keys of `criteria` are member names as the grid knows them; the server resolves them to columns.
 */
export interface GridPageRequest {
  offset: number;
  limit: number;
  sortBy?: string;
  sort?: 'asc' | 'desc';
  criteria?: Record<string, string[]>;
  /**
   * Members of `criteria` whose value is to be looked for within what a row stores rather than compared to it whole,
   * for a column that keeps several values in one string.
   */
  partialKeys?: string[];
}

/**
 * One choice in a picker that points a row at another row of the same type: the id that is stored, and the values
 * shown in its place.
 *
 * The labels are a map rather than a single string because every component declares its own set of them - Ring
 * Terminal shows a GM part number and a supplier part number, Terminal shows a part name and a plating and has no
 * supplier part number at all. The keys are the member names the component's own `MULTI_SELECT_IDS` declaration
 * named, so a new component needs no change here.
 */
export interface ComponentIdOption {
  id: number;
  labels: Record<string, string>;
}

/**
 * Turns a page request into query parameters for the generic object endpoints.
 *
 * Each filter value is sent as its own parameter rather than joined, so a value containing a comma still matches, and
 * `exactFilters` tells the server these are whole values picked from a list rather than something a user typed.
 *
 * @param request       the page, sort and filters to send
 * @param includePaging false for the export, which covers every matching row rather than one page
 */
export function buildGridPageParams(request: GridPageRequest,
                                    includePaging = true): { [key: string]: string | string[] } {
  const params: { [key: string]: string | string[] } = {};
  if (includePaging) {
    params['offset'] = String(request.offset);
    params['limit'] = String(request.limit);
  }
  if (request.sortBy) {
    params['sortBy'] = request.sortBy;
    params['sort'] = request.sort ?? 'asc';
  }
  const criteria = request.criteria ?? {};
  const activeKeys = Object.keys(criteria).filter(key => (criteria[key]?.length ?? 0) > 0);
  for (const key of activeKeys) {
    params[key] = criteria[key];
  }
  if (activeKeys.length > 0) {
    params['exactFilters'] = 'true';
  }
  const partialKeys = (request.partialKeys ?? []).filter(key => activeKeys.includes(key));
  if (partialKeys.length > 0) {
    params['partialFilters'] = partialKeys;
  }
  return params;
}
